import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GenerationResult, StylePreset, Whale } from '@gilding/shared';
import { STYLE_PROFILES } from '@gilding/shared';
import { api } from '../api/client.js';
import { Logo } from '../components/Logo.js';
import { WhalePicker } from '../components/WhalePicker.js';
import { GildedImage } from '../components/GildedImage.js';
import { Reveal } from '../components/Reveal.js';
import { saveImage } from '../lib/saveImage.js';
import { SaveSheet, isCoarsePointer } from '../components/SaveSheet.js';
import { GildingCeremony } from '../components/GildingCeremony.js';
import { ConnectButton } from '../components/ConnectButton.js';
import { ThemeToggle } from '../components/Theme.js';
import { GoldWordmark } from '../components/GoldWordmark.js';
import {
  hasWallet,
  signInWithEthereum,
  signInWithWalletConnect,
  walletConnectAvailable,
  waitForInjectedWallet,
  disconnectWalletConnect,
  detectedWalletNames,
} from '../web3/siwe.js';

/**
 * Drop — a locked, shareable remix page for ONE published piece.
 *
 * Rendered OUTSIDE the app Layout: no nav, no feed, no gallery — the visitor
 * can only sign in and remix THIS scene with their own character, capped per
 * wallet (with a per-IP backstop) on the server. Result is view + download
 * only. This is the link the operator drops into a group chat.
 */
export function Drop() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const campaignQuery = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => api.campaign(id!),
    enabled: !!id,
    retry: false,
  });
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 30_000 });

  const [whale, setWhale] = useState<Whale | null>(null);
  const [result, setResult] = useState<GenerationResult | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const campaign = campaignQuery.data;
  const authed = meQuery.data?.authenticated === true;

  const signIn = async (method: 'injected' | 'walletconnect' = 'injected'): Promise<void> => {
    if (signingIn) return;
    if (method === 'injected' && !hasWallet()) {
      // Wallet in-app browsers can inject late — wait briefly so MetaMask/
      // Trust browsers keep the in-wallet flow before considering QR.
      if (!(await waitForInjectedWallet())) {
        // Genuinely no injected wallet: use the QR path when the build
        // carries WalletConnect instead of dead-ending the visitor.
        if (walletConnectAvailable()) {
          method = 'walletconnect';
        } else {
          setAuthError('No wallet detected. Open this link in your wallet’s in-app browser, or install MetaMask / Rabby / Brave Wallet.');
          return;
        }
      }
    }
    setSigningIn(true);
    setAuthError(null);
    try {
      const out =
        method === 'walletconnect'
          ? // Defer the nonce until the WC session is live (see siwe.ts).
            await signInWithWalletConnect(async () => (await api.nonce()).nonce)
          : await signInWithEthereum((await api.nonce()).nonce);
      await api.verify(out.message, out.signature);
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      await queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    } catch (e) {
      setAuthError(e instanceof Error ? e.message : 'Sign-in failed. Try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const disconnect = async (): Promise<void> => {
    await disconnectWalletConnect();
    try {
      await api.logout();
    } catch {
      /* api.logout clears the local token even if the network call fails */
    }
    setWhale(null);
    setResult(null);
    await queryClient.invalidateQueries({ queryKey: ['me'] });
    await queryClient.invalidateQueries({ queryKey: ['campaign', id] });
  };

  const generateMut = useMutation({
    mutationFn: () => {
      if (!campaign || !campaign.active || !whale) throw new Error('Pick your character first.');
      return api.generate({
        sketch: '',
        stylePreset: campaign.stylePreset as StylePreset,
        mode: 'single',
        aspectRatio: campaign.aspectRatio,
        modelOverride: null,
        referenceImageUrl: whale.imageUrl,
        hasReference: true,
        remixSourceId: campaign.id,
        fixSourceImageUrl: null,
        fixInstruction: null,
        fixReferenceImageUrls: [],
        characterTokenId: whale.tokenId,
      });
    },
    onSuccess: (r) => {
      setResult(r);
      void queryClient.invalidateQueries({ queryKey: ['campaign', id] });
    },
  });

  const remaining =
    campaign && campaign.active ? campaign.remainingForWallet : null;
  const outOfRemixes = remaining !== null && remaining <= 0;
  const genError = generateMut.error instanceof Error ? generateMut.error.message : null;

  return (
    <div className="flex min-h-dvh flex-col">
      {/* Minimal locked header — mark only, no nav anywhere. */}
      <header className="sticky top-0 z-20 bg-surface">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-5 py-3.5">
          <span className="flex items-center gap-3">
            <GoldWordmark className="h-12" />
            <span className="eyebrow whitespace-nowrap text-[9px] sm:text-[11px]">
              A prompt amplifier
            </span>
          </span>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            {authed && (
              <button
                type="button"
                onClick={() => void disconnect()}
                aria-label="Disconnect wallet"
                title="Disconnect wallet"
                className="inline-flex h-9 w-9 items-center justify-center rounded-sm text-ink-soft transition-colors duration-200 hover:text-ink"
              >
                <svg
                  aria-hidden="true"
                  viewBox="0 0 20 20"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 4.5H7a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h5" />
                  <path d="M10.5 10h6.5" />
                  <path d="M14.5 7.5 17 10l-2.5 2.5" />
                </svg>
              </button>
            )}
          </div>
        </div>
        <div aria-hidden className="gild-rule opacity-60" />
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 px-5 py-10">
        {campaignQuery.isLoading ? (
          <p className="font-display italic text-ink-subtle">Opening the drop…</p>
        ) : !campaign || !campaign.active ? (
          <DropEnded />
        ) : result ? (
          <ResultView
            result={result}
            onAgain={!outOfRemixes ? () => setResult(null) : null}
            remaining={remaining}
          />
        ) : (
          <Reveal className="flex flex-col gap-8">
            <header className="flex flex-col gap-3">
              <span className="kicker">A Gilding drop</span>
              <h1 className="heading-large">Make this scene yours.</h1>
              <p className="max-w-prose font-display italic text-lede text-ink-soft">
                Pick your character and we’ll print it into this exact scene: same
                light, same crowd, your whale in the spotlight.
              </p>
            </header>

            {/* The scene being remixed */}
            <figure className="flex flex-col gap-2">
              <div className="card-plate overflow-hidden">
                <GildedImage
                  src={campaign.thumbnail}
                  alt="The scene to remix"
                  className="block w-full"
                />
              </div>
              <figcaption className="tag">
                <span className="tag-dot" />
                {STYLE_PROFILES[campaign.stylePreset as StylePreset]?.label ?? 'Cinematic'}
              </figcaption>
            </figure>

            {!authed ? (
              <section className="flex flex-col items-start gap-4">
                <div className="gild-rule" aria-hidden />
                <p className="font-display italic text-body text-ink-soft">
                  Connect your wallet to make yours. No gas, no email, one signature.
                </p>
                <ConnectButton
                  label="Connect wallet to make yours"
                  busy={signingIn}
                  onClick={() => void signIn()}
                />
                {hasWallet() && (
                  <p className="font-mono text-caption text-ink-subtle">
                    {detectedWalletNames().join(' · ')} detected
                  </p>
                )}
                {authError && (
                  <p className="max-w-prose font-display italic text-caption text-danger">
                    {authError}
                  </p>
                )}
              </section>
            ) : outOfRemixes ? (
              <section className="flex flex-col gap-3">
                <div className="gild-rule" aria-hidden />
                <h2 className="heading-section">You’ve made all of yours</h2>
                <p className="font-display italic text-body text-ink-soft">
                  You’ve used all {campaign.maxPerWallet} of your remixes for this drop.
                  Thanks for playing.
                </p>
              </section>
            ) : (
              <section className="flex flex-col gap-6">
                <div className="gild-rule" aria-hidden />
                <WhalePicker selected={whale} onChange={setWhale} lockToWhale />

                {generateMut.isPending && <GildingCeremony variant="remix" />}
                {genError && (
                  <p className="font-display italic text-caption text-danger">{genError}</p>
                )}

                <div className="flex flex-col items-start gap-2">
                  <button
                    type="button"
                    className="btn-primary w-full sm:w-auto sm:min-w-[16rem]"
                    disabled={!whale || generateMut.isPending}
                    onClick={() => generateMut.mutate()}
                  >
                    {generateMut.isPending ? 'Printing yours…' : 'Make mine'}
                  </button>
                  {remaining !== null && (
                    <p className="font-display italic text-caption text-ink-subtle">
                      {remaining} of {campaign.maxPerWallet} remix
                      {campaign.maxPerWallet === 1 ? '' : 'es'} left
                    </p>
                  )}
                </div>
              </section>
            )}
          </Reveal>
        )}
      </main>

      <footer className="px-5 pb-8 pt-10">
        <p className="text-center font-display italic text-caption text-ink-subtle">
          Made with Gilding
        </p>
      </footer>
    </div>
  );
}

function DropEnded() {
  return (
    <section className="flex flex-col items-center gap-4 py-16 text-center">
      <Logo className="h-12 w-12 opacity-70" />
      <h1 className="heading-section">This drop has ended</h1>
      <p className="max-w-sm font-display italic text-body text-ink-soft">
        The link is no longer accepting new remixes. Catch the next one.
      </p>
    </section>
  );
}

function ResultView({
  result,
  onAgain,
  remaining,
}: {
  result: GenerationResult;
  onAgain: (() => void) | null;
  remaining: number | null;
}) {
  const [savingIdx, setSavingIdx] = useState<number | null>(null);
  const [sheet, setSheet] = useState<{ url: string; filename: string } | null>(null);

  const onSave = async (url: string, idx: number): Promise<void> => {
    const filename = `gilding-${result.id}-${idx + 1}.png`;
    // Touch devices: the long-press viewer is the only dependable route to
    // the photo library (iOS share sheets hide "Save Image" for web files).
    if (isCoarsePointer()) {
      setSheet({ url, filename });
      return;
    }
    setSavingIdx(idx);
    try {
      await saveImage(url, filename);
    } finally {
      setSavingIdx(null);
    }
  };

  return (
    <Reveal className="flex flex-col gap-7">
      <header className="flex flex-col gap-2">
        <span className="kicker">Yours is ready</span>
        <h1 className="heading-large">Here’s your gilding.</h1>
      </header>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        {result.images.map((img, i) => (
          <figure key={`${img.model}-${i}`} className="flex flex-col gap-3">
            <div className="card-plate overflow-hidden">
              <GildedImage
                src={img.url}
                alt={`Your gilding, variant ${i + 1}`}
                className="block h-auto w-full"
              />
            </div>
            <button
              type="button"
              className="btn-secondary"
              disabled={savingIdx !== null}
              onClick={() => void onSave(img.url, i)}
            >
              {savingIdx === i ? 'Saving…' : 'Save image'}
            </button>
          </figure>
        ))}
      </div>

      {sheet && (
        <SaveSheet url={sheet.url} filename={sheet.filename} onClose={() => setSheet(null)} />
      )}

      {onAgain && (
        <div className="flex flex-col items-start gap-2">
          <button type="button" className="btn-ghost" onClick={onAgain}>
            Make another
          </button>
          {remaining !== null && (
            <p className="font-display italic text-caption text-ink-subtle">
              {remaining} left
            </p>
          )}
        </div>
      )}
    </Reveal>
  );
}
